import { Environment } from 'environment.js'
import { get, post, put, remove, removeETag, removeWithPayload } from 'Repository/index.js'
import type { Observable } from 'rxjs'
import { from } from 'rxjs'
import type { IUser } from 'Model/index.js'
import type { IBulkResponse } from 'Framework/Repository/Response/IBulkResponse.js'
import type { IEvaluationProject } from 'Model/Project/IEvaluationProject.js'
import type { IProjectNote } from '../../Model/Project/IProjectNote.js'
import type { ProjectWithCounts } from '../../Model/Project/ProjectWithCounts.js'
import type { IResolvedParticipant } from '../../Model/Project/IResolvedParticipant.js'
import type { IProjectSample, ProjectOrder, ProjectSamplesPage } from '../../Model/Project/IProjectSample.js'
import type { IProjectParticipant } from '../../Model/Project/IProjectParticipant.js'
import type { BlastSource } from '../../Model/Project/BlastSource.js'
import type { IOtuProcessIdDTO } from '../../Model/Project/IOtuProcessIdDTO.js'
import type { IProjectInvite } from '../../Model/Invite/IInvite.js'
import type { ReportRequest } from '../../Generated/ReportRequest.js'
import type { ProjectSortValue } from '../../Model/Project/ProjectSort.js'
import { tracingAxios } from '../Sentry.js'

export const createProject = (
  data: {
    name: string
    description: string
    targetTaxonomy?: string
    redlistMode?: string
    groupingMode?: string
    processingType?: string
  },
  user?: IUser
): Observable<IEvaluationProject> => {
  return post(`${Environment.API_URL}/api/projects`, data, user)
}

export const getProject = (projectId: string): Observable<IEvaluationProject> => {
  return get(`${Environment.API_URL}/api/projects/${projectId}`)
}

export const getAllProjects = (
  data: {
    size: number
    page: number
    filters: { parameter: string; value: unknown; filterType: string }[]
    sort?: ProjectSortValue
  },
  user?: IUser
): Observable<IBulkResponse<ProjectWithCounts>> => {
  return get(`${Environment.API_URL}/api/projects`, data, user)
}

export const downloadReport = (projectId: string, settings: ReportRequest) =>
  from(
    tracingAxios.post<{ link: string }>(`${Environment.API_URL}/api/projects/${projectId}/report`, settings, {
      withCredentials: true
    })
  )

export const activateDraftMode = (projectId: string, data: { version: string }): Observable<string> => {
  return put(`${Environment.API_URL}/api/projects/${projectId}/draftMode`, data)
}

export const deactivateDraftMode = (projectId: string, data: { version: string }): Observable<string> => {
  return removeETag(`${Environment.API_URL}/api/projects/${projectId}/draftMode`, data.version)
}

export const updateProject = (
  projectId: string,
  data: {
    name: string
    description: string
    ownerUser: string
    targetTaxonomy?: string
    redlistMode: string | null
    groupingMode: string | null
    processingType: string | null
    version: string
  },
  user?: IUser
): Observable<IEvaluationProject> => {
  return put(`${Environment.API_URL}/api/projects/${projectId}`, data, user)
}

export const fetchProjectOrders = (projectId: string, user?: IUser): Observable<ProjectOrder[]> => {
  return get(`${Environment.API_URL}/api/projects/${projectId}/orders`, {}, user)
}

export const addProjectSamples = (projectId: string, samples: string[], user?: IUser): Observable<IProjectSample[]> => {
  return post(`${Environment.API_URL}/api/projects/${projectId}/samples/`, { samples }, user)
}

export const removeProjectSample = (projectId: string, projectSampleId: number, user?: IUser): Observable<IEvaluationProject> => {
  return remove(`${Environment.API_URL}/api/projects/${projectId}/samples/${projectSampleId}`, user)
}

export const removeProjectSamples = (projectId: string, sampleIds: number[], user?: IUser): Observable<number> => {
  return removeWithPayload(`${Environment.API_URL}/api/projects/${projectId}/samples/`, sampleIds, user)
}

export const getProjectSamples = (projectId: string, user?: IUser): Observable<IProjectSample[]> => {
  return get(`${Environment.API_URL}/api/projects/${projectId}/samples`, user)
}

export const getProjectSamplesPage = (projectId: string, params: { limit?: number; offset?: number }, user?: IUser) =>
  get<typeof params, ProjectSamplesPage>(`${Environment.API_URL}/api/projects/${projectId}/samples`, params, user)

export const deleteProject = (projectId: string, user?: IUser): Observable<number> => {
  return remove(`${Environment.API_URL}/api/projects/${projectId}`, user)
}

// Project Notes
export const getNotes = (projectId: string, user?: IUser): Observable<IProjectNote[]> => {
  return get(`${Environment.API_URL}/api/projects/${projectId}/notes`, {}, user)
}

export const addNote = (projectId: string, data: { content: string }, user?: IUser): Observable<IProjectNote[]> => {
  return post(`${Environment.API_URL}/api/projects/${projectId}/notes`, data, user)
}

export const deleteNote = (projectId: string, noteId: number, user?: IUser): Observable<IProjectNote[]> => {
  return remove(`${Environment.API_URL}/api/projects/${projectId}/notes/${noteId}`, user)
}

export const updateNote = (projectId: string, noteId: number, data: { content: string }, user?: IUser): Observable<IProjectNote[]> => {
  return put(`${Environment.API_URL}/api/projects/${projectId}/notes/${noteId}`, data, user)
}

export const getParticipants = (projectId: string, user?: IUser): Observable<IResolvedParticipant[]> => {
  return get(`${Environment.API_URL}/api/projects/${projectId}/participants/`, {}, user)
}

export const removeParticipant = (projectId: string, participantId: number, user?: IUser): Observable<IEvaluationProject> => {
  return remove(`${Environment.API_URL}/api/projects/${projectId}/participants/${participantId}`, user)
}

export const updateParticipant = (
  projectId: string,
  participantId: number,
  data: {
    mayEdit: boolean
    version: string
  },
  user?: IUser
): Observable<IProjectParticipant> => {
  return put(`${Environment.API_URL}/api/projects/${projectId}/participants/${participantId}`, data, user)
}

export const getOtuProcessIds = (projectId: string, blastType: BlastSource, user?: IUser): Observable<IOtuProcessIdDTO> => {
  return get(`${Environment.API_URL}/api/projects/${projectId}/evaluations/otus/${blastType}`, {}, user)
}

/**
 * Accept a project invite by ID
 */
export const acceptProjectInvite = (projectId: string, inviteId: number, user?: IUser): Observable<IProjectInvite> => {
  return post(`${Environment.API_URL}/api/projects/${projectId}/invites/${inviteId}/accept`, {}, user)
}
